/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Row de pago (mobile+desk) — unificada a TicketListRow (Square).
 * El layout custom mobile con DenseListCard fue eliminado en la migración Square.
 */
import React from 'react';
import { TicketListRow } from '../../shared/ticket/TicketListRow';
import type { ExpenseSettlementListRow } from '../domain/expenseTypes';
import {
  expenseSettlementMethodLabel,
  expenseSettlementStatusLabel,
  formatExpenseDateShort,
  formatExpenseMoney,
} from '../domain/expenseLabels';
import { formatLocalDateTime } from '@shared/vueltoDateTime';

type Props = {
  row: ExpenseSettlementListRow;
  onPress: () => void;
};

function ExpensePaymentHistoryRowCardInner({ row, onPress }: Props) {
  const isVoided = row.settlementStatus === 'voided';
  // Misma meta que ExpensePaymentsTable: fecha/hora · categoría · proveedor ·
  // nota · anulado; "gasto DD/MM" solo si la factura es de otro día.
  const paidDay = (row.paidAt ?? '').slice(0, 10);
  const metaParts = [
    formatLocalDateTime(row.paidAt),
    row.categoryName,
    row.supplierName,
    row.expenseDate && row.expenseDate.slice(0, 10) !== paidDay
      ? `gasto ${formatExpenseDateShort(row.expenseDate)}`
      : null,
  ].filter(Boolean);
  if (row.note) metaParts.push(row.note);
  if (isVoided && row.voidReason) metaParts.push(`anulado: ${row.voidReason}`);

  return (
    <TicketListRow
      title={expenseSettlementMethodLabel(row.method)}
      meta={metaParts.join(' · ')}
      value={formatExpenseMoney(row.settlementAmount)}
      statusLabel={expenseSettlementStatusLabel(row.settlementStatus)}
      statusTone={isVoided ? 'muted' : 'ok'}
      inactive={isVoided}
      onPress={onPress}
      accessibilityLabel={[
        isVoided ? 'Anulado' : null,
        expenseSettlementMethodLabel(row.method),
        formatExpenseMoney(row.settlementAmount),
        formatLocalDateTime(row.paidAt),
        row.supplierName,
      ]
        .filter(Boolean)
        .join(', ')}
      testID={`gastos.payment.row.${row.settlementId}`}
    />
  );
}

export const ExpensePaymentHistoryRowCard = React.memo(ExpensePaymentHistoryRowCardInner);
